import { IFiscalClient, SimpleResult } from "@prostocheck/shared";

import { DOCUMENT_REQUEST_ORIGINAL_XML, runCommand } from "../client";
import { FISCAL_COMMANDS } from "./commands.constants";
import {
  IDocumentQuery,
  IDocumentResponse,
  ILastShiftTotalsResponse,
  IObjectsResponse,
  IRegistrarStateResponse,
  IShiftDocumentsResponse,
  IShiftsQuery,
  IShiftsResponse,
} from "./commands.models";

/**
 * One signed call yields every business unit and every ПРРО of the taxpayer
 * whose certificate signed it. This is the whole "who am I and what do I own"
 * step — there is no account, no login and no taxpayer id in the protocol.
 */
export const requestObjects = (
  client: IFiscalClient,
): Promise<SimpleResult<IObjectsResponse | null>> =>
  runCommand<IObjectsResponse>({
    client,
    command: { Command: FISCAL_COMMANDS.objects },
    isSigned: true,
  });

export const requestRegistrarState = ({
  client,
  registrarFiscalNumber,
}: {
  client: IFiscalClient;
  registrarFiscalNumber: string;
}): Promise<SimpleResult<IRegistrarStateResponse | null>> =>
  runCommand<IRegistrarStateResponse>({
    client,
    command: {
      Command: FISCAL_COMMANDS.registrarState,
      NumFiscal: registrarFiscalNumber,
      IncludeTaxObject: true,
    },
    isSigned: true,
  });

export const requestShifts = ({
  client,
  registrarFiscalNumber,
  from,
  to,
}: { client: IFiscalClient } & IShiftsQuery): Promise<
  SimpleResult<IShiftsResponse | null>
> =>
  runCommand<IShiftsResponse>({
    client,
    command: {
      Command: FISCAL_COMMANDS.shifts,
      NumFiscal: registrarFiscalNumber,
      From: from,
      To: to,
    },
    isSigned: true,
  });

export const requestShiftDocuments = ({
  client,
  registrarFiscalNumber,
  shiftId,
}: {
  client: IFiscalClient;
  registrarFiscalNumber: string;
  shiftId: number;
}): Promise<SimpleResult<IShiftDocumentsResponse | null>> =>
  runCommand<IShiftDocumentsResponse>({
    client,
    command: {
      Command: FISCAL_COMMANDS.documents,
      NumFiscal: registrarFiscalNumber,
      ShiftId: shiftId,
    },
    isSigned: true,
  });

/**
 * `Type: 1` asks for the seller's original XML — the only representation that
 * parses without unwrapping a CMS envelope first.
 */
export const requestCheck = ({
  client,
  registrarFiscalNumber,
  documentFiscalNumber,
}: { client: IFiscalClient } & IDocumentQuery): Promise<
  SimpleResult<IDocumentResponse | null>
> =>
  runCommand<IDocumentResponse>({
    client,
    command: {
      Command: FISCAL_COMMANDS.checkExt,
      RegistrarNumFiscal: registrarFiscalNumber,
      NumFiscal: documentFiscalNumber,
      Type: DOCUMENT_REQUEST_ORIGINAL_XML,
      AcquireCabinetUrl: true,
    },
    isSigned: false,
  });

export const requestZReport = ({
  client,
  registrarFiscalNumber,
  documentFiscalNumber,
}: { client: IFiscalClient } & IDocumentQuery): Promise<
  SimpleResult<IDocumentResponse | null>
> =>
  runCommand<IDocumentResponse>({
    client,
    command: {
      Command: FISCAL_COMMANDS.zReportExt,
      RegistrarNumFiscal: registrarFiscalNumber,
      NumFiscal: documentFiscalNumber,
      Type: DOCUMENT_REQUEST_ORIGINAL_XML,
    },
    isSigned: true,
  });

/** Totals of a shift that has no Z-report yet — the only view of an open shift. */
export const requestLastShiftTotals = ({
  client,
  registrarFiscalNumber,
}: {
  client: IFiscalClient;
  registrarFiscalNumber: string;
}): Promise<SimpleResult<ILastShiftTotalsResponse | null>> =>
  runCommand<ILastShiftTotalsResponse>({
    client,
    command: {
      Command: FISCAL_COMMANDS.lastShiftTotals,
      NumFiscal: registrarFiscalNumber,
    },
    isSigned: true,
  });

/** Connectivity probe; needs no key, so it also proves the gateway works. */
export const requestServerState = (
  client: IFiscalClient,
): Promise<SimpleResult<{ UID: string; Timestamp: string } | null>> =>
  runCommand<{ UID: string; Timestamp: string }>({
    client,
    command: { Command: FISCAL_COMMANDS.serverState },
    isSigned: false,
  });
